import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, Printer, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { printTableWithHeader } from "../../../utils/printUtils";
import { getFieldOptions } from "../../../utils/fieldsOptions";
import "./CurrentTalentPoolPage.css";

const PAGE_SIZE = 30;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function CurrentTalentPoolPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");
  const [talents, setTalents] = useState([]);
  const [serverTime, setServerTime] = useState({ fullDateTime: "" });
  const [currentUser, setCurrentUser] = useState({ fullname: "", systemRole: "" });

  // Load current user
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) setCurrentUser({ fullname: user.fullname, systemRole: user.systemRole });
  }, []);

  // Fetch approved talents & server time
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [talentRes, timeRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/talent/approved`),
          axios.get(`${API_BASE_URL}/time/datetime`),
        ]);
        setTalents(talentRes.data || []);
        if (timeRes.data.success) setServerTime(timeRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setTimeout(() => setLoading(false), 1200);
      }
    };
    fetchData();
  }, []);

  // Filter + search talents
  const filteredTalents = useMemo(() => {
    return talents.filter((t) => {
      const matchesSearch =
        t.name?.toLowerCase().includes(search.toLowerCase()) ||
        t.qualification?.toLowerCase().includes(search.toLowerCase());
      const matchesField = !fieldFilter || t.field === fieldFilter;
      return matchesSearch && matchesField;
    });
  }, [talents, search, fieldFilter]);

  const totalPages = Math.ceil(filteredTalents.length / PAGE_SIZE);
  const paginatedData = filteredTalents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Navigation handlers
  const handleView = (id) => navigate(`/admin-panel/talent/view/${id}`);

  // Print
  const handlePrint = () => {
    printTableWithHeader({
      title: "Current Talent Pool",
      summary: "List of all approved talents",
      columns: [
        { key: "sn", label: "SN" },
        { key: "name", label: "Name" },
        { key: "field", label: "Field" },
        { key: "qualification", label: "Qualification" },
      ],
      rows: filteredTalents.map((t, idx) => ({
        sn: idx + 1,
        name: t.name,
        field: t.field,
        qualification: t.qualification,
      })),
    });
  };

  // Export Excel
  const handleExportExcel = () => {
    const dataForExcel = filteredTalents.map((t, idx) => ({
      SN: idx + 1,
      Name: t.name,
      Field: t.field,
      Qualification: t.qualification,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CurrentTalents");
    XLSX.writeFile(workbook, "current_talents.xlsx");
  };

  return (
    <div className="pending-alumni-page">
      {/* USER INFO */}
      <div className="pending-user-card">
        <div className="pending-user-left">
          <div className="pending-user-name">Welcome, {currentUser.fullname}</div>
          <div className="pending-user-role">System Role: {currentUser.systemRole}</div>
        </div>
        <div className="pending-user-right">
          <div className="pending-user-date">{formatDate(serverTime.fullDateTime)}</div>
          <div className="pending-user-time">{formatTime(serverTime.fullDateTime)}</div>
        </div>
      </div>

      <h1 className="pending-page-title">Current Talent Pool</h1>

      {/* ACTION BUTTONS */}
      <div className="pending-action-row">
        <button className="pending-btn-primary" onClick={handlePrint}>
          <Printer size={16} /> Print
        </button>
        <button className="pending-btn-secondary" onClick={handleExportExcel}>
          <FileSpreadsheet size={16} /> Export
        </button>
      </div>

      {/* FILTER */}
      <div className="pending-filters">
        <select value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)}>
          <option value="">Filter by Field</option>
          {getFieldOptions().map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>

      {/* SEARCH */}
      <input
        className="pending-search-input"
        placeholder="Search by Name or Qualification"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* TABLE */}
      <div className="pending-table-card">
        <div className="pending-table-meta">
          <span>Total Talents: {filteredTalents.length}</span>
          <span>Page {page} of {totalPages || 1}</span>
        </div>

        {loading ? (
          <div className="pending-loading">Loading talents…</div>
        ) : (
          <table className="pending-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Name</th>
                <th>Field</th>
                <th>Qualification</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((t, idx) => (
                <tr key={t._id}>
                  <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                  <td>{t.name}</td>
                  <td>{t.field}</td>
                  <td>{t.qualification}</td>
                  <td className="actions">
                    <Eye size={16} color="var(--ug-blue)" onClick={() => handleView(t._id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* PAGINATION */}
        <div className="pending-pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}